import { MessageSquare, Send, X } from "lucide-react";
import { type CSSProperties, FormEvent, useEffect, useState } from "react";
import { submitFeedback, type FeedbackInput } from "../lib/portalApi";

const categories: Array<{ value: FeedbackInput["category"]; label: string }> = [
  { value: "bug", label: "Something is broken" },
  { value: "idea", label: "Idea or improvement" },
  { value: "question", label: "Question" },
  { value: "other", label: "Other" }
];

type FeedbackCategory = FeedbackInput["category"] | "";

export function FeedbackWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [category, setCategory] = useState<FeedbackCategory>("");
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bottomOffset, setBottomOffset] = useState(22);

  useEffect(() => {
    function updateBottomOffset() {
      const footer = document.querySelector<HTMLElement>(".portal-footer");
      if (!footer) {
        setBottomOffset(22);
        return;
      }
      const footerTop = footer.getBoundingClientRect().top;
      const safeGap = 18;
      const baseOffset = 22;
      const overlapOffset = Math.max(baseOffset, window.innerHeight - footerTop + safeGap);
      setBottomOffset(Math.ceil(overlapOffset));
    }

    updateBottomOffset();
    window.addEventListener("scroll", updateBottomOffset, { passive: true });
    window.addEventListener("resize", updateBottomOffset);
    return () => {
      window.removeEventListener("scroll", updateBottomOffset);
      window.removeEventListener("resize", updateBottomOffset);
    };
  }, []);
  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setStatus(null);
    setError(null);

    if (message.trim().length < 5) {
      setError("Please add a little more detail so we can understand the feedback.");
      return;
    }
    if (!category) {
      setError("Please choose a feedback category.");
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await submitFeedback({
        category,
        message: message.trim(),
        pageUrl: window.location.href
      });
      setStatus(result.message);
      setCategory("");
      setMessage("");
    } catch {
      setError("We could not send feedback right now. Please try again in a moment.");
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="feedback-widget" style={{ "--feedback-bottom-offset": `${bottomOffset}px` } as CSSProperties}>
      {isOpen ? (
        <section className="feedback-panel" aria-label="Prescriber feedback">
          <div className="feedback-panel-header">
            <div>
              <p className="eyebrow">Feedback</p>
              <h2>Tell us what you noticed</h2>
            </div>
            <button className="icon-button" type="button" aria-label="Close feedback" onClick={() => setIsOpen(false)}>
              <X size={18} />
            </button>
          </div>
          <form onSubmit={handleSubmit}>
            <label>
              Category
              <select value={category} onChange={(event) => setCategory(event.target.value as FeedbackCategory)}>
                <option value="">Select a category...</option>
                {categories.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Feedback
              <textarea
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                placeholder="Share what happened or what would make the portal better."
                rows={5}
              />
            </label>
            {status ? <p className="form-success" role="status">{status}</p> : null}
            {error ? <p className="form-alert" role="alert">{error}</p> : null}
            <div className="feedback-actions">
              <button className="secondary-button" type="button" onClick={() => setIsOpen(false)}>
                Cancel
              </button>
              <button className="primary-button" type="submit" disabled={isSubmitting}>
                <Send size={16} />
                {isSubmitting ? "Sending..." : "Send feedback"}
              </button>
            </div>
          </form>
        </section>
      ) : null}
      <button className="feedback-fab" type="button" onClick={() => setIsOpen((value) => !value)}>
        <MessageSquare size={18} />
        Feedback
      </button>
    </div>
  );
}


